/**
 * AI Ranger — a self-contained TinyMCE plugin that adds AI editing actions to
 * the press-release body editor.
 *
 * A toolbar button, a floating selection toolbar, and a right-click menu offer
 * AI actions. With a selection, only that text is sent; with no selection, the
 * whole document is. Requests go to the `press-releases.ai-assist` endpoint
 * (gpt-4o); the response is shown as an inline diff with per-change
 * Accept/Reject and a Diff/Preview toggle (see review.js).
 *
 * The menu currently exposes only "Custom prompt..."; Fix grammar / Change tone
 * / Change length are stubbed in actionItems() for incremental rollout.
 */

import { startReview, isReviewing } from "./review";

const WAND_ICON =
    '<svg width="24" height="24" viewBox="0 0 24 24">' +
    '<path fill="currentColor" d="M7.5 5.6 10 7 8.6 4.5 10 2 7.5 3.4 5 2l1.4 2.5L5 7zm12 9.8L17 14l1.4 2.5L17 19l2.5-1.4L22 19l-1.4-2.5L22 14zM22 2l-2.5 1.4L17 2l1.4 2.5L17 7l2.5-1.4L22 7l-1.4-2.5zm-7.63 5.29a.996.996 0 0 0-1.41 0L1.29 18.96a.996.996 0 0 0 0 1.41l2.34 2.34c.39.39 1.02.39 1.41 0L16.7 11.05a.996.996 0 0 0 0-1.41l-2.33-2.35zm-1.03 5.49-2.12-2.12 2.44-2.44 2.12 2.12-2.44 2.44z"/></svg>';

/* ------------------------------------------------------------------ *
 * Backend AI call
 * ------------------------------------------------------------------ */

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
}

/**
 * Send an AI action to the backend and resolve with { html, interactionId }.
 * Rejects with Error('missing-pr-id') when the press release has no id yet
 * (e.g. an unsaved draft), or with the server message on failure.
 */
function requestAi(editor, action, content, opts = {}) {
    const prId = editor.getElement().dataset.prId;
    if (!prId) {
        return Promise.reject(new Error("missing-pr-id"));
    }

    return window.axios
        .post(
            `/press-releases/${prId}/ai-assist`,
            { action, content, ...opts },
            { headers: { "X-CSRF-TOKEN": csrfToken() } }
        )
        .then((response) => {
            if (!response.data || response.data.success !== true) {
                throw new Error(response.data?.message || "AI request failed");
            }
            return {
                html: response.data.html,
                interactionId: response.data.interaction_id,
            };
        });
}

/**
 * Report the inline-review outcome for an interaction (accept/reject counts).
 * Fire-and-forget — a failed report should never disrupt the editor.
 */
function reportOutcome(interactionId, summary) {
    if (!interactionId) {
        return;
    }
    window.axios
        .patch(`/ai-interactions/${interactionId}/outcome`, summary, {
            headers: { "X-CSRF-TOKEN": csrfToken() },
        })
        .catch(() => {});
}

/* ------------------------------------------------------------------ *
 * Custom prompt dialog
 * ------------------------------------------------------------------ */

function openPromptDialog(editor) {
    editor.windowManager.open({
        title: "AI Assistant",
        body: {
            type: "panel",
            items: [
                {
                    type: "textarea",
                    name: "instruction",
                    label: "What should the AI do with the selected text?",
                    placeholder: "e.g. Rewrite this as a punchy opening line",
                },
            ],
        },
        initialData: { instruction: "" },
        buttons: [
            { type: "cancel", text: "Cancel" },
            { type: "submit", text: "Generate", primary: true },
        ],
        onSubmit: (dialog) => {
            const { instruction } = dialog.getData();
            dialog.close();
            run(editor, "freeform", { instruction });
        },
    });
}

/* ------------------------------------------------------------------ *
 * Action runner
 * ------------------------------------------------------------------ */

/**
 * Runs an AI action and opens inline review with the result.
 *
 * With a selection, only the selected text is sent to the AI; with no
 * selection, the whole document is sent. Either way the suggestion is diffed
 * against the full document body so review markup lands in the right place.
 */
function run(editor, action, opts = {}) {
    if (isReviewing()) {
        editor.notificationManager.open({
            type: "warning",
            text: "Finish reviewing the current AI changes first.",
            timeout: 2500,
        });
        return;
    }

    const hasSelection = !editor.selection.isCollapsed();
    const isInsert = action === "continue";
    const content = hasSelection
        ? editor.selection.getContent({ format: "html" })
        : editor.getContent();
    const scope = hasSelection ? "selection" : "document";
    const bodyOld = editor.getContent();
    const bookmark = editor.selection.getBookmark(2);

    editor.setProgressState(true);

    requestAi(editor, action, content, { ...opts, scope })
        .then(({ html: suggestion, interactionId }) => {
            let bodyNew;
            if (!hasSelection && !isInsert) {
                bodyNew = suggestion;
            } else {
                // Apply to the selection/cursor to get the full new body, read
                // it, then undo so the editor is restored before review starts.
                editor.undoManager.transact(() => {
                    editor.selection.moveToBookmark(bookmark);
                    if (isInsert) {
                        editor.selection.collapse(false);
                        editor.insertContent(suggestion);
                    } else {
                        editor.selection.setContent(suggestion);
                    }
                });
                bodyNew = editor.getContent();
                editor.undoManager.undo();
            }

            editor.setProgressState(false);
            startReview(editor, {
                bodyOld,
                bodyNew,
                onResolve: (summary) => reportOutcome(interactionId, summary),
            });
        })
        .catch((error) => {
            editor.setProgressState(false);
            const text =
                error && error.message === "missing-pr-id"
                    ? "Save the press release before using AI."
                    : "The AI request failed. Please try again.";
            editor.notificationManager.open({ type: "error", text, timeout: 4000 });
        });
}

/**
 * The shared list of AI actions, reused by the toolbar menu button, the
 * selection toolbar, and the right-click menu.
 */
function actionItems(editor) {
    const item = (action, text, opts) => ({
        type: "menuitem",
        text,
        onAction: () => run(editor, action, opts),
    });

    return [
        item("fix", "Fix grammar & spelling"),
        item("shorten", "Shorten"),
        {
            type: "menuitem",
            text: "Custom prompt...",
            onAction: () => openPromptDialog(editor),
        },
    ];
}

/* ------------------------------------------------------------------ *
 * Status-bar "Ask AI assistant" button (replaces the TinyMCE branding)
 * ------------------------------------------------------------------ */

let statusbarStyleEl = null;

function ensureStatusbarStyles() {
    if (statusbarStyleEl) {
        return;
    }
    statusbarStyleEl = document.createElement("style");
    statusbarStyleEl.textContent = `
.air-statusbar-btn { margin-left:auto; display:inline-flex; align-items:center; gap:5px;
    background:none; border:0; cursor:pointer; padding:0 6px; font-size:12px; line-height:1;
    color:#2f6df6; font-weight:600; font-family:inherit; }
.air-statusbar-btn:hover { color:#255ad6; }
.air-statusbar-btn svg { width:14px; height:14px; }
`;
    document.head.appendChild(statusbarStyleEl);
}

function addStatusbarButton(editor) {
    const statusbar = editor.getContainer()?.querySelector(".tox-statusbar");
    if (!statusbar || statusbar.querySelector(".air-statusbar-btn")) {
        return;
    }
    ensureStatusbarStyles();

    const button = document.createElement("button");
    button.type = "button";
    button.className = "air-statusbar-btn";
    button.innerHTML = `${WAND_ICON}<span>Ask AI assistant</span>`;
    button.addEventListener("click", () => openPromptDialog(editor));

    const resizeHandle = statusbar.querySelector(".tox-statusbar__resize-handle");
    if (resizeHandle) {
        statusbar.insertBefore(button, resizeHandle);
    } else {
        statusbar.appendChild(button);
    }
}

/* ------------------------------------------------------------------ *
 * Plugin registration
 * ------------------------------------------------------------------ */

window.tinymce.PluginManager.add("airanger", function (editor) {
    editor.ui.registry.addIcon("airanger", WAND_ICON);

    editor.on("init", () => addStatusbarButton(editor));

    // Toolbar button + floating selection toolbar both use this menu button.
    editor.ui.registry.addMenuButton("airanger", {
        icon: "airanger",
        tooltip: "AI Assistant",
        fetch: (callback) => callback(actionItems(editor)),
    });

    // Right-click submenu — only offered when text is selected.
    editor.ui.registry.addNestedMenuItem("airanger_menu", {
        text: "AI Assistant",
        icon: "airanger",
        getSubmenuItems: () => actionItems(editor),
    });
    editor.ui.registry.addContextMenu("airanger", {
        update: () => (editor.selection.isCollapsed() ? "" : "airanger_menu"),
    });

    // Floating toolbar that appears over a text selection.
    editor.ui.registry.addContextToolbar("airanger", {
        predicate: () => !editor.selection.isCollapsed(),
        position: "selection",
        scope: "node",
        items: "airanger",
    });

    return {
        getMetadata: () => ({ name: "AI Ranger", url: "" }),
    };
});
